import { useEffect, useRef } from "react";
import type { FxState, PortraitRect } from "./FluidReveal";

/**
 * Independent audio-visual layer. Reads shared pointer / transformation state
 * and draws restrained waveform trails, speed ripples, ear-driver pulses,
 * signal pulses and sonic rings. Portrait-relative anchors are PLACEHOLDERS
 * until the final robot artwork is supplied.
 */

export interface AudioFXSettings {
  wave: number; // waveform trail + ripples
  pulse: number; // circuit pulses
  trail: boolean;
  ripples: boolean;
  drivers: boolean;
  circuits: boolean;
  rings: boolean;
}

// Normalised to the portrait image (0..1). PLACEHOLDER positions.
const EARS = [{ x: 0.372, y: 0.306 }, { x: 0.615, y: 0.306 }];
const HEAD = { x: 0.493, y: 0.26 };
const PATHS: [number, number][][] = [
  [[0.5, 0.56], [0.5, 0.68], [0.36, 0.76], [0.26, 0.9]],
  [[0.5, 0.56], [0.5, 0.68], [0.64, 0.76], [0.74, 0.9]],
  [[0.44, 0.55], [0.34, 0.6], [0.24, 0.66]],
  [[0.56, 0.55], [0.66, 0.6], [0.76, 0.66]],
];

interface Props { fx: React.MutableRefObject<FxState>; rect: PortraitRect; settings: AudioFXSettings }

const AudioFX = ({ fx, rect, settings }: Props) => {
  const ref = useRef<HTMLCanvasElement>(null);
  const sRef = useRef(settings); sRef.current = settings;
  const rRef = useRef(rect); rRef.current = rect;

  useEffect(() => {
    const c = ref.current!;
    const ctx = c.getContext("2d")!;
    let raf = 0;
    const trail: { x: number; y: number; v: number }[] = [];
    const ripples: { x: number; y: number; age: number; s: number }[] = [];
    let lastSpawn = 0;
    let moving = 0; // eased "was moving" amount, for the echo ring when the cursor comes to rest
    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      c.width = c.clientWidth * dpr; c.height = c.clientHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    resize();
    const ro = new ResizeObserver(resize); ro.observe(c);
    const start = performance.now();

    const loop = () => {
      raf = requestAnimationFrame(loop);
      const s = sRef.current, f = fx.current, R = rRef.current;
      const t = (performance.now() - start) / 1000;
      ctx.clearRect(0, 0, c.clientWidth, c.clientHeight);
      const P = (p: { x: number; y: number }) => ({ x: R.x + p.x * R.w + f.parX * 6, y: R.y + p.y * R.h + f.parY * -6 });

      if (f.active) trail.push({ x: f.x, y: f.y, v: f.v });
      if (trail.length > 36 || (!f.active && trail.length)) trail.shift();

      // waveform trail: three thin oscillating strands following the cursor
      if (s.trail && trail.length > 3 && s.wave > 0) {
        ctx.globalCompositeOperation = "source-over";
        for (let k = 1; k < 2; k++) {
          ctx.beginPath();
          for (let i = 1; i < trail.length; i++) {
            const a = trail[i - 1], b = trail[i];
            const dx = b.x - a.x, dy = b.y - a.y, len = Math.hypot(dx, dy) || 1;
            const nx = -dy / len, ny = dx / len;
            const amp = (2 + Math.min(b.v, 1) * 5) * s.wave * (i / trail.length);
            const o = Math.sin(i * 0.45 - t * 2.6 + k * 2.1) * amp * 0.6;
            const x = b.x + nx * o, y = b.y + ny * o;
            if (i === 1) ctx.moveTo(x, y); else ctx.lineTo(x, y);
          }
          ctx.strokeStyle = "rgba(120,130,145,0.22)";
          ctx.lineWidth = 0.8;
          ctx.stroke();
        }
      }

      // echo: one slow ring like the tail of a note when the cursor comes to rest
      if (f.active && f.v > 0.25) moving = Math.min(1, moving + 0.05);
      else if (f.active && moving > 0.6 && f.v < 0.05) { ripples.push({ x: f.x, y: f.y, age: 0, s: 0.8 }); moving = 0; }
      // speed ripples (slow, soft)
      if (s.ripples && f.active && f.v > 0.6 && t - lastSpawn > 1.2) {
        ripples.push({ x: f.x, y: f.y, age: 0, s: Math.min(f.v, 1) }); lastSpawn = t;
      }
      ctx.globalCompositeOperation = "source-over";
      for (let i = ripples.length - 1; i >= 0; i--) {
        const r = ripples[i]; r.age += 1 / 60;
        if (r.age > 3.2) { ripples.splice(i, 1); continue; }
        for (let k = 0; k < 2; k++) {
          const rad = (r.age * 32 + k * 10) * (0.6 + r.s);
          ctx.beginPath(); ctx.arc(r.x, r.y, rad, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(120,130,145,${(1 - r.age / 3.2) * 0.12 * s.wave})`;
          ctx.lineWidth = 0.6; ctx.stroke();
        }
      }

      ctx.globalCompositeOperation = "source-over";
      const near = (p: { x: number; y: number }) => f.active ? Math.max(0, 1 - Math.hypot(p.x - f.x, p.y - f.y) / 420) : 0;

      // ear drivers: slow, long, soft rings
      if (s.drivers) EARS.forEach((e) => {
        const p = P(e), k = Math.max(f.full, near(p));
        if (k < 0.02) return;
        for (let i = 0; i < 3; i++) {
          const ph = (t * 0.45 + i / 3) % 1;
          const ease = 1 - Math.pow(1 - ph, 2);
          ctx.beginPath(); ctx.arc(p.x, p.y, 4 + ease * R.w * 0.08, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(140,180,230,${Math.pow(1 - ph, 2) * 0.3 * k})`; ctx.lineWidth = 0.7; ctx.stroke();
        }
      });

      // sonic rings behind the head during full reveal
      if (s.rings && f.full > 0.05) {
        const p = P(HEAD);
        for (let i = 0; i < 6; i++) {
          const ph = (t * 0.25 + i / 6) % 1;
          ctx.beginPath(); ctx.arc(p.x, p.y, R.w * (0.18 + ph * 0.5), 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(30,32,36,${(1 - ph) * 0.12 * f.full})`; ctx.lineWidth = 0.7; ctx.stroke();
        }
      }

      // signal pulses through pathways
      if (s.circuits && s.pulse > 0) PATHS.forEach((path, pi) => {
        const pts = path.map(([x, y]) => P({ x, y }));
        const mid = pts[Math.floor(pts.length / 2)];
        const k = Math.max(f.full, near(mid)) * s.pulse;
        if (k < 0.02) return;
        const segs: number[] = []; let total = 0;
        for (let i = 1; i < pts.length; i++) { const l = Math.hypot(pts[i].x - pts[i - 1].x, pts[i].y - pts[i - 1].y); segs.push(l); total += l; }
        let d = ((t * 0.35 + pi * 0.27) % 1) * total;
        for (let i = 0; i < segs.length; i++) {
          if (d <= segs[i]) {
            const a = pts[i], b = pts[i + 1], u = d / segs[i];
            const x = a.x + (b.x - a.x) * u, y = a.y + (b.y - a.y) * u;
            const g = ctx.createRadialGradient(x, y, 0, x, y, 10);
            g.addColorStop(0, `rgba(255,150,70,${Math.min(1, 0.95 * k)})`); g.addColorStop(0.25, `rgba(255,120,40,${0.55 * k})`); g.addColorStop(1, "rgba(255,140,60,0)");
            ctx.fillStyle = g; ctx.beginPath(); ctx.arc(x, y, 10, 0, Math.PI * 2); ctx.fill();
            break;
          }
          d -= segs[i];
        }
      });
    };
    loop();
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [fx]);

  return <canvas ref={ref} className="pointer-events-none absolute inset-0 h-full w-full" aria-hidden />;
};

export default AudioFX;
